import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary component to catch and handle React component errors
 * Prevents the entire app from crashing due to component errors
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to error reporting service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="backdrop-blur-2xl bg-gradient-to-br from-white/10 to-white/5 rounded-3xl border border-white/20 p-12">
              <div className="flex items-center justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-red-500 blur-xl opacity-50 animate-pulse"></div>
                  <AlertTriangle className="w-16 h-16 text-red-400 relative z-10" />
                </div>
              </div>

              <h1 className="text-4xl font-bold mb-4 text-center bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Oops! Something went wrong
              </h1>

              <p className="text-gray-400 text-center mb-8 text-lg">
                We encountered an unexpected error. Don't worry, it's not your fault.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <details className="mb-8 p-6 rounded-2xl bg-black/50 border border-red-500/30">
                  <summary className="cursor-pointer text-red-400 font-semibold mb-4 hover:text-red-300 transition-colors">
                    Error Details (Development Only)
                  </summary>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-300 mb-2">Error Message:</h3>
                      <pre className="text-xs text-red-300 overflow-x-auto p-3 rounded bg-black/50 border border-red-500/20">
                        {this.state.error.toString()}
                      </pre>
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-300 mb-2">Component Stack:</h3>
                        <pre className="text-xs text-gray-400 overflow-x-auto p-3 rounded bg-black/50 border border-gray-500/20">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={this.handleReset}
                  className="relative group px-8 py-4 rounded-2xl overflow-hidden transform hover:scale-105 transition-all duration-300"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 animate-gradient-x"></div>
                  <span className="relative text-lg font-semibold flex items-center justify-center gap-2">
                    <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
                    Try Again
                  </span>
                </button>

                <button
                  onClick={() => window.location.href = '/'}
                  className="relative group px-8 py-4 rounded-2xl backdrop-blur-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-105"
                >
                  <span className="text-lg font-semibold">
                    Go to Homepage
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
